import { OfferService } from '../../../../services/offer/offer.service';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Offer } from '../../../../models/offer.model';
import { Page } from '../../../../models/page.model';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { SnackbarService } from '../../../../services/shared/snackbar/snackbar.service';
import { DialogService } from '../../../../services/shared/dialog/dialog.service';
import { TranslationService } from '../../../../services/translation/translation.service';
import { UtilsService } from '../../../../services/shared/utils/utils.service';
import { AssociatePageModalComponent } from '../../../../shared/components/associate-page-modal/associate-page-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { PageService } from '../../../../services/page/page.service';
import { MatSidenav } from '@angular/material/sidenav';
import { PageType } from '../../../../shared/enums/page-type/page-type.module';
import { Subject, takeUntil } from 'rxjs';
import { CommonService } from '../../../../services/shared/common/common.service';
import { GenerateCustomerOfferModal } from '../../../../shared/components/generate-customer-offer-modal/generate-customer-offer-modal.component';
import { LoadingService } from '../../../../services/loading/loading.service';
import { MatChipInputEvent } from '@angular/material/chips';

@Component({
  selector: 'app-offer',
  templateUrl: './offer-details.component.html',
  styleUrl: './offer-details.component.css'
})
export class OfferDetailsComponent implements OnInit, AfterViewInit, OnDestroy {
  private destroy$ = new Subject<void>();
  dataSource = new MatTableDataSource<Page>();
  PageType = PageType;

  totalItems = 0;
  offerPages: Offer = {
    name: "",
    code: [],
    pages: []
  };
  offerDetails: Offer | null = null;
  offerId: string | null = "";
  displayedColumns: string[] = ['name', 'type','order', 'actions'];
  allPages: Page[] = [];
  availablePages: Page[] = [];

  codeExists: boolean = false;
  listCodes: string[] = [];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('rightSidenav') rightSidenav!: MatSidenav;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private offerService: OfferService,
    private pageService: PageService,
    private dialog: MatDialog,
    private dialogService: DialogService,
    private snackBar: SnackbarService,
    public utils: UtilsService,
    public common: CommonService,
    public translationService: TranslationService,
    private loadingService: LoadingService
  ) { }

  ngOnInit(): void {
    this.offerId = this.route.snapshot.paramMap.get('id');
    if (this.offerId) {
      this.getOfferPages(this.offerId);
    }
    if (!this.allPages || this.allPages.length <= 0) {
      this.getAllPages();
    }

     this.offerService.getAllOffersCodes()
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (result: string[]) => {
        this.listCodes = result;
      },
      error: (error) => {
        this.snackBar.showError(this.translationService.translate('Error fetching offers codes'));
      }
    });
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initializeTableData(pages: Page[]) {
    this.dataSource = new MatTableDataSource<Page>(pages);
    this.totalItems = pages.length;
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  getOfferPages(offerId: string) {
    if (!offerId) {
      this.snackBar.showError('Offer ID is invalid.');
      return;
    }

    this.loadingService.show();

    this.offerService.getOfferPages(offerId)
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (result: Offer) => {
        this.loadingService.hide();
        this.offerPages = result;

        if (!this.offerDetails) {
          this.offerDetails = result;
        }

        const parentPages = this.common.orderPages(result.pages);

        this.initializeTableData(parentPages);
        this.calculateAvailablePages();
      },
      error: (error) => {
        this.loadingService.hide();
        this.snackBar.showError(this.translationService.translate('Error fetching offer'));
      }
    });
  }
  getAllPages() {
    this.loadingService.show();
    this.pageService.getAllPages()
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (result: Page[]) => {
        this.loadingService.hide();
        this.allPages = result;
        this.calculateAvailablePages();
      },
      error: (error) => {
        this.loadingService.hide();
        this.snackBar.showError(this.translationService.translate('Error fetching offer'));
      }
    });
  }

  deleteOfferPage(e: MouseEvent, page: Page): void {
    e.preventDefault();
    e.stopPropagation();

    this.dialogService
    .confirm(this.translationService.translate('Confirm Delete'), this.translationService.translate('Are you sure you want to delete this offer page?'))
    .then((confirmed) => {
      if (confirmed) {
        if (this.offerId && page.id) {
          this.offerService.disassociateOfferPage(this.offerId, page.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              if (this.offerId != null && this.offerId != "") {
                this.snackBar.showSuccess(this.translationService.translate('Offer page deleted successfully'));
                this.getOfferPages(this.offerId);
              }
            },
            error: (error) => {
              this.snackBar.showError(this.translationService.translate('Error fetching offer'));
            }
          });
        } else {
          this.snackBar.showError(this.translationService.translate('ID is null'));
        }
      }
    });
  }

  navigateToPage(page: Page) {
    this.router.navigate(['/admin/pages/', page.id]);
  }

  onSaveOffer(): void {
    if (!this.offerId || !this.offerDetails || !this.offerDetails.name) {
      this.snackBar.showError(this.translationService.translate('Invalid offer data.'));
      return;
    }

    this.offerService.updateOffer(this.offerDetails, this.offerId)
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: () => {
        this.snackBar.showSuccess(this.translationService.translate('Success on saving this offer'));
      },
      error: (error) => {
        this.snackBar.showError(this.translationService.translate(error.error));
      }
    });
  }

  associateOfferPage(pageId: string): void {
    if (!this.offerId || !pageId) {
      this.snackBar.showError(this.translationService.translate('Invalid page or offer ID.'));
      return;
    }

    this.offerService.associateOfferPage(this.offerId, pageId)
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: () => {
        this.getOfferPages(this.offerId!);
        this.snackBar.showSuccess(this.translationService.translate('Success on saving this offer'));
      },
      error: (error) => {
        this.snackBar.showError(this.translationService.translate('Error updating offer'));
      }
    });
  }

  onAssociatePage(): void {
    if (this.availablePages.length === 0) return;

    this.calculateAvailablePages();

    const dialogRef = this.dialog.open(AssociatePageModalComponent, {
      width: '500px',
      data: { sources: this.availablePages }
    });

    dialogRef.afterClosed().subscribe(newPage => {
      if (newPage && this.offerPages!.pages) {
        this.associateOfferPage(newPage.id);
      }
      this.calculateAvailablePages();
    });
  }

  calculateAvailablePages(): void {
    if (this.allPages && this.allPages.length > 0) {
      if (this.offerPages && this.offerPages.pages && this.offerPages.pages.length > 0) {
        const offerPageIds = this.offerPages.pages.map(page => page.id);

        this.availablePages = this.allPages.filter(page =>
          !offerPageIds.includes(page.id)
        );
      }
      else {
        this.availablePages = this.allPages;
      }
    }
  }

  onCreateOfferForCustomer(){
    this.dialog.open(GenerateCustomerOfferModal, {
      width: '500px',
      data: {
        offerCodes: this.offerDetails?.code
      }
    });
  }

  blockDecimal(event: KeyboardEvent) {
    this.utils.blockDecimal(event);
  }

  normalizeExpirationDelay() {
    this.offerDetails!.expirationDelay = this.utils.normalizeNumber(this.offerDetails!.expirationDelay);
  }

  onInputChange(event: Event){
    const result = this.common.handleInputCode(event, this.listCodes);
    this.codeExists = result.exists;
  }
  onAddCode(event: MatChipInputEvent) {
    const added = this.common.addCode(
      event.value || '',
      this.offerDetails!.code,
      this.listCodes,
      this.utils
    );

    if (added){
      event.chipInput!.clear();
    }
  }
  onRemoveCode(code: string): void {
    this.common.removeCode(code, this.offerDetails!.code, this.listCodes);
  }
}
